import { useMemo } from "react";
import { useQueries } from "react-query";
import axios from "axios";
import {
  blockRegistry,
  extractAllBlocksFromTpl,
  isTplAnnotationEnabled,
} from "@okf/ootils";
import { useGetQueryData } from "@/utils/react-query-hooks/general";

/**
 * Per-collection BE aggregation for the legacy (non-SM, non-plugin) annotation
 * explorer bar graph. One parallel call per annotation-enabled tag collection
 * across the selected tpls. Replaces the heavy `useGetAnnotations` + JS reduce.
 */
export const useLegacyAnnotationBarGraphData = ({
  enabled,
  activeFilters,
  selectedTPLsArrayOptions,
}) => {
  const allTpls = useGetQueryData("allTpls") || [];
  const platformConfigs = useGetQueryData("platformConfigs");
  const smTagTypesConfig =
    platformConfigs?.SELF_MANAGED_BASE_CONFIGS?.annotation_tagTypesConfig || null;

  const annoTagCollections = useMemo(() => {
    const selectedContentTypes = (selectedTPLsArrayOptions || []).map(
      (o) => o.value
    );
    const selectedTpls = allTpls.filter((tpl) =>
      selectedContentTypes.includes(tpl.kp_content_type)
    );

    const collections = new Set();
    selectedTpls.forEach((tpl) => {
      if (!isTplAnnotationEnabled(tpl)) return;
      const allBlocks = extractAllBlocksFromTpl({ tpl });
      blockRegistry.getAnnotationEnabledBlocks(allBlocks).forEach((block) => {
        blockRegistry
          .getTagTypesConfig(block, smTagTypesConfig)
          .forEach((conf) => {
            if (conf?.tagType) collections.add(conf.tagType);
          });
      });
    });
    return Array.from(collections);
  }, [allTpls, selectedTPLsArrayOptions, smTagTypesConfig]);

  const mergedActiveFilters = useMemo(
    () => [
      {
        filterId: "contentTypeFilter_contentType",
        values: selectedTPLsArrayOptions,
        target: { filterType: "valuePathType", valuePath: "meta.contentType" },
      },
      ...(activeFilters || []),
    ],
    [activeFilters, selectedTPLsArrayOptions]
  );

  const queries = useQueries(
    annoTagCollections.map((collection) => {
      const body = {
        contentType: "annotations",
        dimensions: [`annotations.tags.${collection}`],
        activeFilters: mergedActiveFilters,
      };
      return {
        queryKey: ["annotationCollectionCounts", collection, body],
        queryFn: () =>
          axios
            .post("/api/insightMatrix/aggrMetricsAcrossDimensions", body, {
              headers: { "Content-Type": "application/json" },
            })
            .then((r) => r.data),
        enabled: enabled && annoTagCollections.length > 0,
      };
    })
  );

  const data = useMemo(() => {
    const collectionsWithRows = annoTagCollections.map((collection, i) => {
      const result = queries[i]?.data;
      const rows = Array.isArray(result?.data)
        ? result.data
        : Array.isArray(result)
          ? result
          : [];
      const total = rows.reduce((sum, r) => sum + (r.count || 0), 0);
      return { collection, rows, total };
    });

    return collectionsWithRows
      .sort((a, b) => b.total - a.total)
      .flatMap(({ collection, rows }) =>
        rows.map((r) => ({
          category: collection,
          display: r.display,
          value: r.count,
        }))
      );
  }, [queries, annoTagCollections]);

  const status = (() => {
    if (queries.length === 0) return enabled ? "success" : "idle";
    if (queries.some((q) => q.status === "error")) return "error";
    if (queries.some((q) => q.status === "loading")) return "loading";
    if (queries.every((q) => q.status === "success")) return "success";
    return "idle";
  })();

  const error = queries.find((q) => q.error)?.error || null;
  const isLoading = queries.some((q) => q.isLoading);

  return { data, status, error, isLoading };
};
